<template>
  <div class="p-4">
    <div class="row g-4 mb-5">
      <div class="col-md-4">
        <div class="card p-4 text-center">
          <div class="text-muted">{{ $t("incomeStatement.totalIncome") }}</div>
          <div class="value">{{ formatCurrency(totalIncome) }}</div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card p-4 text-center">
          <div class="text-muted">{{ $t("incomeStatement.totalExpense") }}</div>
          <div class="value negative">{{ formatCurrency(totalExpense) }}</div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card p-4 text-center">
          <div class="text-muted">{{ $t("incomeStatement.netResult") }}</div>
          <div
            class="value"
            :class="totalProfit >= 0 ? 'positive' : 'negative'"
          >
            {{ formatCurrency(totalProfit) }}
          </div>
        </div>
      </div>
    </div>

    <div class="card p-4">
      <div class="table-responsive">
        <table class="table table-bordered align-middle text-end mb-0">
          <thead>
            <tr>
              <th class="text-start">{{ $t("incomeStatement.category") }}</th>
              <th v-for="(cat, index) in results.categories" :key="index">
                {{ cat }}
              </th>
              <th>{{ $t("incomeStatement.total") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, rowIndex) in results.rows"
              :key="rowIndex"
              :class="{
                'table-secondary': row.type === 'header',
                'table-light fw-bold': row.type === 'total',
                'table-success fw-bold': row.type === 'profit',
                clickable:
                  row.type === 'expense' ||
                  row.type === 'income' ||
                  row.key === 'logistics_shipping' ||
                  row.key === 'shipping_income',
              }"
            >
              <td
                class="text-start"
                :class="{
                  'ps-4': row.type !== 'header',
                  'fw-bold': row.type === 'header',
                }"
              >
                {{ row.label }}
              </td>
              <template v-if="row.type === 'header'">
                <td :colspan="results.categories.length + 1"></td>
              </template>
              <template v-else>
                <td
                  v-for="(cat, valIndex) in results.categories"
                  :key="valIndex"
                  @click="handleRowClick(row, valIndex)"
                  :class="{
                    negative:
                      row.type === 'expense' ||
                      (row.type === 'profit' &&
                        (row.values[valIndex] ?? 0) < 0),
                    positive:
                      row.type === 'profit' && (row.values[valIndex] ?? 0) >= 0,
                  }"
                >
                  {{ formatCurrency(row.values[valIndex] ?? 0) }}
                </td>
                <td
                  @click="handleRowClick(row)"
                  :class="{
                    negative:
                      row.type === 'expense' ||
                      (row.type === 'profit' && row.total < 0),
                    positive: row.type === 'profit' && row.total >= 0,
                    'fw-bold': true,
                  }"
                >
                  {{ formatCurrency(row.total) }}
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <ShippingDetailModal ref="shippingDetailModal" />
    <IncomeItemsModal ref="incomeItemsModal" />
  </div>
</template>

<script>
import http from "@/modules/http";
import { useStore } from "vuex";
import ShippingDetailModal from "@/components/modals/ShippingDetailModal.vue";
import IncomeItemsModal from "@/components/modals/IncomeItemsModal.vue";

export default {
  name: "IncomeStatement",
  components: {
    ShippingDetailModal,
    IncomeItemsModal,
  },
  data() {
    return {
      store: useStore(),
      results: {
        currency: "",
        categories: [],
        rows: [],
      },
    };
  },
  computed: {
    date() {
      return this.store.state.topBar.date;
    },
    totalIncome() {
      const row = this.results.rows.find((r) => r.key === "total_net_revenue");
      return row ? row.total : 0;
    },
    totalExpense() {
      return this.results.rows
        .filter((r) => r.type === "expense")
        .reduce((sum, r) => sum + (r.total || 0), 0);
    },
    totalProfit() {
      const row = this.results.rows.find((r) => r.type === "profit");
      return row ? row.total : 0;
    },
  },
  watch: {
    date: {
      deep: true,
      handler() {
        this.loadData();
    },
  },
  beforeDestroy() {
    // Clean up event listeners
    if (this.$refs.incomeItemsModal) {
      this.$refs.incomeItemsModal.$off('items-added');
    }
  },
  },
  mounted() {
    this.loadData();
    // Listen for items added event to refresh data
    if (this.$refs.incomeItemsModal) {
      this.$refs.incomeItemsModal.$on('items-added', () => {
        this.loadData();
      });
    }
  },
   methods: {
     handleRowClick(row) {
       console.log("Clicked row:", row); // Debugging line
       if (row.key === 'logistics_shipping' || row.key === 'shipping_income') {
         this.$refs.shippingDetailModal.show(row.key);
       } else if (row.type === 'expense' || row.type === 'income') {
         // Show income items modal for income/expense categories
         // Pass the row ID as category_id for regular categories
         const categoryId = (row.key === 'other_income' || row.key === 'other_fees') ? null : row.id;
         this.$refs.incomeItemsModal.show(row, categoryId);
       }
     },
    loadData() {
      console.log(this.date);
      const from = this.date[0].toISOString().split("T")[0];
      const to = this.date[1].toISOString().split("T")[0];
      http
        .fetch(`/income-statement/data?from=${from}&to=${to}`)
        .then((data) => {
          this.currency = data.currency;
          this.results = data;
        });
    },
    formatCurrency(value) {
      if (value === null) return "";
      return new Intl.NumberFormat("hu-HU", {
        style: "currency",
        currency: this.currency,
        maximumFractionDigits: 0,
      }).format(value);
    },
  },
};
</script>
<style scoped>
.value {
  font-size: 1.6rem;
  font-weight: 700;
}
.positive {
  color: #16a34a;
}
.negative {
  color: #dc2626;
}
table {
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}
thead {
  background: linear-gradient(90deg, #2563eb, #3b82f6);
  color: #fff;
}
tbody tr:hover {
  background-color: rgba(59, 130, 246, 0.1);
}
td,
th {
  vertical-align: middle;
}
.clickable {
  cursor: pointer;
}
.clickable:hover {
  background-color: rgba(59, 130, 246, 0.15) !important;
}
</style>
